import { CandidateRole } from "src/app/models/enum";
import { ManeuverOutcomeDraft } from "src/app/models/models";
import { ObEventForResetFilter } from "../../shared-v2/utils/observation.utils";
import { outcomeSlotForObColumn } from "./ts1-rating-outcome-interaction";

function roleKey(role?: CandidateRole | null): string {
	if (role === CandidateRole.PF || role === CandidateRole.PM) return role;
	return "_none_";
}

function eventTime(event: ObEventForResetFilter): number {
	return new Date(event.createdAt).getTime();
}

/** Positive observed attempts (not N/A). A 0/N unobserve is scored separately. */
export function hasTs1V3ObValueForRating(event: ObEventForResetFilter): boolean {
	if (event.isDisabled) return false;
	// isRemoved marks the last action (decrement), not zero value — keep rows with active attempts.
	return (event.activeAttempt ?? 0) > 0;
}

/**
 * Include in Rating A/B newObs inputs (mirrors BE audit replay).
 * Keeps valued roles and 0/N unobserves so How Often does not treat presets as untouched.
 * isRemoved may be false after RESET-from-N/A restores a 0/N snapshot.
 * N/A and inert 0/0 are omitted.
 */
export function includeTs1V3ObInRatingNewObs(event: ObEventForResetFilter): boolean {
	if (event.isDisabled) return false;
	const active = event.activeAttempt ?? 0;
	const total = event.totalAttempt ?? 0;
	if (active > 0) return true;
	return total > 0;
}

/** `maneuverCode::observationCode::role` for PF/PM N/A tracking; null when role is missing. */
export function ts1V3NaRoleObKey(
	maneuverCode: string,
	observationCode: string,
	role?: CandidateRole | string | null,
): string | null {
	const rk = roleKey(role as CandidateRole | null | undefined);
	if (rk === "_none_") return null;
	return `${maneuverCode}::${observationCode}::${rk}`;
}

/**
 * Latest-per-role N/A keys. Used to drop that role's predefined attempts from How Often expected
 * (PF N/A must not keep PF's admin preset in the denominator).
 */
export function buildTs1V3NaRoleObKeys<T extends ObEventForResetFilter>(events: T[]): Set<string> {
	const groups = new Map<string, T[]>();

	for (const event of events ?? []) {
		const key = obGroupKey(event);
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key)!.push(event);
	}

	const na = new Set<string>();
	for (const group of groups.values()) {
		for (const row of latestByRoleForGroup(group).values()) {
			if (!row.isDisabled) continue;
			const key = ts1V3NaRoleObKey(row.maneuverCode, row.observationCode, row.candidateRole);
			if (key) na.add(key);
		}
	}
	return na;
}

export function isTs1V3ConfigObsExcludedByNa(params: {
	maneuverCode: string;
	observationCode: string;
	candidateRole?: CandidateRole | string | null;
	naRoleKeys: Set<string>;
}): boolean {
	const key = ts1V3NaRoleObKey(params.maneuverCode, params.observationCode, params.candidateRole);
	return key != null && params.naRoleKeys.has(key);
}

function hasObValue(event: ObEventForResetFilter): boolean {
	return hasTs1V3ObValueForRating(event);
}

function obGroupKey(event: ObEventForResetFilter): string {
	return `${event.candidateId}::${event.maneuverCode}::${event.observationCode}`;
}

function latestByRoleForGroup<T extends ObEventForResetFilter>(group: T[]): Map<string, T> {
	const latestByRole = new Map<string, T>();
	for (const event of group) {
		const key = roleKey(event.candidateRole);
		const prev = latestByRole.get(key);
		if (!prev || eventTime(event) >= eventTime(prev)) {
			latestByRole.set(key, event);
		}
	}
	return latestByRole;
}

/**
 * OB is fully excluded from predefined/unobserved pool only when both PF and PM
 * latest roles exist and every one is N/A. Single-role N/A (no sibling event)
 * must not wipe the other role's admin preset — use naRoleKeys instead.
 */
export function buildTs1V3FullyDisabledObKeys<T extends ObEventForResetFilter>(events: T[]): Set<string> {
	const groups = new Map<string, T[]>();

	for (const event of events ?? []) {
		const key = obGroupKey(event);
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key)!.push(event);
	}

	const disabled = new Set<string>();
	for (const group of groups.values()) {
		const latestByRole = latestByRoleForGroup(group);
		const pfPmRoles = [...latestByRole.entries()]
			.filter(([key]) => key !== "_none_")
			.map(([, row]) => row);
		// Need both PF and PM present; PF-only N/A must keep PM preset via naRoleKeys.
		if (pfPmRoles.length < 2) continue;
		if (pfPmRoles.every((row) => !!row.isDisabled)) {
			const sample = pfPmRoles[0];
			disabled.add(`${sample.maneuverCode}::${sample.observationCode}`);
		}
	}

	return disabled;
}

/**
 * Collapse add/remove OB events for TS1 Rating A/B inputs.
 *
 * Rules:
 * - Emit the latest row per seat that still scores: activeAttempt > 0, or a
 *   0/N unobserve (activeAttempt === 0 and totalAttempt > 0).
 * - A sibling seat that still has attempts must not drop that unobserve.
 *   Submit's audit replay keeps it; dropping it makes the pending scorer
 *   treat the preset as untouched.
 * - Drop N/A and inert 0/0.
 * - When no seat still scores, keep the latest row so the OB stays excluded.
 */
export function mergeTs1V3ObEventsForRatingCalc<T extends ObEventForResetFilter>(events: T[]): T[] {
	const groups = new Map<string, T[]>();

	for (const event of events ?? []) {
		const key = obGroupKey(event);
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key)!.push(event);
	}

	const merged: T[] = [];

	for (const group of groups.values()) {
		const latestByRole = latestByRoleForGroup(group);
		const scoredRoles = [...latestByRole.values()].filter(includeTs1V3ObInRatingNewObs);

		if (scoredRoles.length > 0) {
			merged.push(...scoredRoles);
			continue;
		}

		const overallLatest = [...group].sort((a, b) => eventTime(b) - eventTime(a))[0];
		merged.push(overallLatest);
	}

	return merged;
}

export function isTs1V3ManeuverRoleOutcomeNa(params: {
	maneuverOutcomes: ManeuverOutcomeDraft[] | undefined;
	maneuverCode: string;
	candidateRole?: CandidateRole | string | null;
	candidateIndex: number;
	hasSecondCandidate: boolean;
}): boolean {
	const { maneuverOutcomes, maneuverCode, candidateRole, candidateIndex, hasSecondCandidate } =
		params;
	if (!maneuverOutcomes?.length || !maneuverCode) return false;
	if (candidateRole !== CandidateRole.PF && candidateRole !== CandidateRole.PM) return false;
	const outcome = maneuverOutcomes.find((row) => row.maneuverCode === maneuverCode);
	if (!outcome) return false;
	const side: "left" | "right" = candidateIndex === 0 ? "left" : "right";
	const slot = outcomeSlotForObColumn(side, candidateRole, hasSecondCandidate);
	return outcome[slot] === "na";
}

/** Mark OB events disabled when that candidate's Maneuver Outcome seat is N/A. */
export function applyManeuverOutcomeNaDisabledToEvents<T extends ObEventForResetFilter>(
	events: T[],
	maneuverOutcomes: ManeuverOutcomeDraft[] | undefined,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): T[] {
	if (!maneuverOutcomes?.length) return events;
	return (events ?? []).map((event) => {
		if (
			!isTs1V3ManeuverRoleOutcomeNa({
				maneuverOutcomes,
				maneuverCode: event.maneuverCode,
				candidateRole: event.candidateRole,
				candidateIndex,
				hasSecondCandidate,
			})
		) {
			return event;
		}
		return { ...event, isDisabled: true };
	});
}
